import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  ChevronRight,
  Circle,
  Check
} from 'lucide-react';
import { useLang } from '../../core/lang';
import LangKeys from '../../core/locales/keys';
import { getCustomerLedger } from '../../services/dashboardApi';
import WhatsAppCreditsPage from '../settings/WhatsAppCreditsPage';

const SmsSettingsView = ({ party, onBack }) => {
  const { t, currentLang } = useLang();
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedLang, setSelectedLang] = useState(null);
  const [showLangModal, setShowLangModal] = useState(false);
  const [showCredits, setShowCredits] = useState(false);

  const languages = [
    { code: null, name: t(LangKeys.follow_app) },
    { code: 'hi', name: 'हिन्दी (Hindi)' },
    { code: 'en', name: 'English' },
    { code: 'en_in', name: 'Hinglish' },
    { code: 'gu', name: 'ગુજરાતી (Gujarati)' },
    { code: 'mr', name: 'मराठी (Marathi)' },
    { code: 'pa', name: 'ਪੰਜਾਬੀ (Punjabi)' },
    { code: 'ta', name: 'தமிழ் (Tamil)' },
    { code: 'te', name: 'తెలుగు (Telugu)' }
  ];

  useEffect(() => {
    fetchData();
  }, [party.id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`https://khata.moxpal.com/api/v2/index.php?path=khata/customers/${party.id}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      const json = await res.json();

      if (json.success) {
        const val = json.customer?.whatsapp_enabled;
        setEnabled(val === 1 || val === "1" || val === true);
        const savedLang = localStorage.getItem('sms_lang');
        setSelectedLang(savedLang || null);
      }
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (val) => {
    if (saving) return;
    setSaving(true);
    setEnabled(val);
    try {
      const token = localStorage.getItem('token');
      await fetch(`https://khata.moxpal.com/api/v2/index.php?path=whatsapp/toggle-customer&customer_id=${party.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ enabled: val ? 1 : 0 })
      });
    } catch (err) {
      console.error('Toggle error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSelectLang = async (langCode) => {
    setSelectedLang(langCode);
    setShowLangModal(false);
    try {
      if (!langCode) localStorage.removeItem('sms_lang');
      else localStorage.setItem('sms_lang', langCode);

      const token = localStorage.getItem('token');
      await fetch(`https://khata.moxpal.com/api/v2/index.php?path=whatsapp/language`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ lang: langCode || '' })
      });
    } catch (err) {
      console.error('Lang update error:', err);
    }
  };

  const getLangName = (lang) => {
    if (!lang) return `${t(LangKeys.follow_app)} (${languages.find(l => l.code === currentLang)?.name || currentLang})`;
    return languages.find(l => l.code === lang)?.name || lang;
  };

  if (showCredits) {
    return <WhatsAppCreditsPage onBack={() => setShowCredits(false)} />;
  }

  return (
    <div style={styles.container}>
      {/* HEADER */}
      <div style={styles.appBar}>
        <button style={styles.backBtn} onClick={onBack}>
          <ArrowLeft size={20} color="#FFFFFF" />
        </button>
        <h3 style={styles.appBarTitle}>{t(LangKeys.sms_settings)}</h3>
      </div>

      {loading ? (
        <div style={styles.center}><div className="spinner"></div></div>
      ) : (
        <div style={styles.scrollContent}>
          <div style={styles.card}>
            <div style={styles.customerRow}>
              <div style={styles.avatar}>{party.name[0].toUpperCase()}</div>
              <span style={styles.customerName}>{party.name}</span>
            </div>
          </div>

          <div style={styles.card}>
            <div style={styles.toggleRow}>
              <div style={styles.toggleTextCol}>
                <span style={styles.toggleTitle}>{t(LangKeys.send_whatsapp_messages)}</span>
                <div style={styles.statusRow}>
                  <Circle size={10} fill={enabled ? "#25D366" : "#EF4444"} stroke="none" />
                  <span style={{ ...styles.statusText, color: enabled ? "#25D366" : "#EF4444" }}>
                    {enabled ? t(LangKeys.enabled) : t(LangKeys.disabled)}
                  </span>
                </div>
              </div>

              <div
                style={{ ...styles.switch, background: enabled ? '#25D366' : '#E2E8F0' }}
                onClick={() => handleToggle(!enabled)}
              >
                {saving ? (
                  <div className="spinner-small" style={{ width: '16px', height: '16px', margin: '4px' }}></div>
                ) : (
                  <div style={{ ...styles.switchHandle, transform: enabled ? 'translateX(20px)' : 'translateX(0)' }} />
                )}
              </div>
            </div>
          </div>

          <div style={styles.card} onClick={() => setShowLangModal(true)}>
            <div style={styles.menuItem}>
              <div style={styles.menuTextCol}>
                <span style={styles.menuTitle}>{t(LangKeys.message_language)}</span>
                <span style={styles.menuSub}>{getLangName(selectedLang)}</span>
              </div>
              <ChevronRight size={18} color="#94A3B8" />
            </div>
          </div>

          {enabled && (
            <div style={styles.card} onClick={() => setShowCredits(true)}>
              <div style={styles.menuItem}>
                <div style={styles.menuTextCol}>
                  <span style={styles.menuTitle}>{t(LangKeys.manage_whatsapp_settings)}</span>
                  <span style={styles.menuSub}>{t(LangKeys.recharge_and_control)}</span>
                </div>
                <ChevronRight size={18} color="#94A3B8" />
              </div>
            </div>
          )}
        </div>
      )}

      {showLangModal && (
        <div style={styles.modalOverlay} onClick={() => setShowLangModal(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h4 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>🌐 {t(LangKeys.select_language)}</h4>
            </div>
            <div style={styles.langGrid}>
              {languages.filter(l => l.code !== null).map(lang => {
                const isActive = (selectedLang === lang.code) || (!selectedLang && lang.code === currentLang);
                const shortCodes = { en: 'En', hi: 'हि', en_in: 'Hi', gu: 'ગુ', mr: 'म', pa: 'ਪੰ', ta: 'த', te: 'తె' };
                return (
                  <div
                    key={lang.code}
                    style={{ ...styles.langItem, borderColor: isActive ? '#2563EB' : '#D1D5DB', background: isActive ? '#EFF6FF' : '#FFFFFF' }}
                    onClick={() => handleSelectLang(lang.code)}
                  >
                    <div style={styles.shortBox}>{shortCodes[lang.code] || lang.code?.substring(0, 2)}</div>
                    <span style={styles.langLabel}>{lang.name.split(' ')[0]}</span>
                    {isActive && <Check size={14} color="#2563EB" style={{ marginLeft: 'auto' }} />}
                  </div>
                );
              })}
            </div>
            <button style={styles.closeBtn} onClick={() => setShowLangModal(false)}>{t(LangKeys.close)}</button>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { height: '100%', display: 'flex', flexDirection: 'column', background: '#F3F4F6', borderRadius: '24px', overflow: 'hidden', position: 'relative' },
  appBar: { height: '64px', background: '#1D4ED8', display: 'flex', alignItems: 'center', padding: '0 16px', gap: '16px', flexShrink: 0 },
  backBtn: { background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' },
  appBarTitle: { color: '#FFFFFF', fontSize: '18px', fontWeight: '700', margin: 0 },
  scrollContent: { flex: 1, overflowY: 'auto', padding: '16px' },
  card: { background: '#FFFFFF', borderRadius: '16px', padding: '16px', marginBottom: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.03)', cursor: 'pointer' },
  customerRow: { display: 'flex', alignItems: 'center', gap: '12px' },
  avatar: { width: '48px', height: '48px', background: '#1D4ED8', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFFFFF', fontWeight: '800', fontSize: '18px' },
  customerName: { fontSize: '16px', fontWeight: '800', color: '#111827' },
  toggleRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  toggleTextCol: { display: 'flex', flexDirection: 'column', gap: '4px' },
  toggleTitle: { fontSize: '15px', fontWeight: '800', color: '#111827' },
  statusRow: { display: 'flex', alignItems: 'center', gap: '6px' },
  statusText: { fontSize: '13px', fontWeight: '700' },
  switch: { width: '44px', height: '24px', borderRadius: '12px', padding: '2px', cursor: 'pointer', transition: 'all 0.3s ease', position: 'relative' },
  switchHandle: { width: '20px', height: '20px', background: '#FFFFFF', borderRadius: '50%', transition: 'all 0.3s ease' },
  menuItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  menuTextCol: { display: 'flex', flexDirection: 'column', gap: '2px' },
  menuTitle: { fontSize: '15px', fontWeight: '800', color: '#111827' },
  menuSub: { fontSize: '13px', color: '#94A3B8', fontWeight: '600' },
  modalOverlay: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.35)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modalContent: { width: '90%', maxWidth: '420px', background: '#FFFFFF', borderRadius: '22px', padding: '20px', boxShadow: '0 10px 24px rgba(0,0,0,0.15)' },
  modalHeader: { marginBottom: '18px', textAlign: 'center' },
  langGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  langItem: { padding: '8px 12px', borderRadius: '14px', display: 'flex', alignItems: 'center', fontSize: '14px', fontWeight: '600', cursor: 'pointer', border: '1px solid #D1D5DB', transition: 'all 0.2s' },
  shortBox: { width: '32px', height: '32px', background: '#F1F5F9', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '700', marginRight: '10px', color: '#111827' },
  langLabel: { fontSize: '14px', fontWeight: '600', color: '#111827' },
  closeBtn: { width: '100%', height: '44px', marginTop: '20px', background: 'transparent', border: '1px solid #D1D5DB', borderRadius: '12px', color: '#111827', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  center: { flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }
};

export default SmsSettingsView;